import find from "lodash/find";
import React, {
  useState,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { State } from "state";
import loadingSvg from "assets/img/loading.svg";
import successSvg from "assets/img/success.svg";
import { Folder } from "lucide-react";
import axios from "axios";
import {
  checkExistingFiles,
  fileCheckFailed,
  getSASURLUpload,
  updateUploadedFiles
} from "state/create_file_upload";
import { fetchProject } from "state/project";

// Import store upload state actions
import {
  setStoreUploadProject,
  setStoreUploadProgress,
  setStoreLoading,
  setStoreError,
  clearStoreUploads,
} from "state/store-upload-state";

import config from "config";
import { Typography } from "@material-ui/core";
import { fetchAllProjectFiles } from "state/files";

interface Step2UploadAttachmentsProps {
  userId: any;
  projectId: any;
  fileStatus: string;
  projectName?: string;
  projectType?: string;
  disabled?: boolean;
}

interface AnalyticsData {
  attachmentsCount: number;
  fileExtensionList: string[];
  overwrite: boolean;
  projectName: string;
  projectType: string;
  fileStatus: string;
  fileType: string;
}

// Define the ref type that will be exposed to parent
export interface Step2UploadAttachmentsRef {
  triggerUpload: () => Promise<void>;
  getSelectedFileNames: () => string[];
  clearFiles: () => void;
  highlightMismatchedFiles: (fileNames: string[]) => void;
}

const acceptedStoreFileTypes = ".pdf, .psa";

// Helper function to normalize file names by replacing # with __
const normalizeFileName = (name: string): string => {
  return name?.replace(/#/g, '__');
};

const uploadChunk = async (
  chunk: Blob,
  sasUrl: string,
  blockId: string,
  blobAxios: any
) => {
  const arrayBuffer = await chunk.arrayBuffer();
  const uint8Array = new Uint8Array(arrayBuffer);

  const blockUrl = `${sasUrl}&comp=block&blockid=${encodeURIComponent(blockId)}`;

  if (blobAxios.defaults.headers.common["Authorization"]) {
    delete blobAxios.defaults.headers.common["Authorization"];
  }

  await blobAxios.put(blockUrl.toString(), uint8Array);
};

const Step2UploadAttachments = forwardRef<
  Step2UploadAttachmentsRef,
  Step2UploadAttachmentsProps
>(
  (
    {
      userId,
      projectId,
      fileStatus,
      projectName = "",
      projectType = "",
      disabled = false,
    },
    ref
  ) => {
    const dispatch = useDispatch();
    const [analytics, setAnalytics] = useState<AnalyticsData | undefined>();
    const [dragActive, setDragActive] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [finalUploadMessage, setFinalUploadMessage] = useState<string>("");
    const [mismatchedFiles, setMismatchedFiles] = useState<Set<string>>(new Set());

    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const project = useSelector((state: State) => state.project.content);
    const { hasError, loading, content, errors } = useSelector((state: State) => ({
      hasError: state.ATTACHMENT_file_upload.hasError,
      loading: state.ATTACHMENT_file_upload.loading,
      content: state.ATTACHMENT_file_upload.content,
      errors: state.ATTACHMENT_file_upload.errors,
    }));

    const storeUploadState = useSelector((state: State) => state.storeUpload);
    const { uploadedFiles = [], uploadProgress = {} } = storeUploadState || {};

    // Load files from localStorage on mount
    useEffect(() => {
      const storageKey = `attachments_${projectId}_${fileStatus}`;
      const savedFileNames = localStorage.getItem(storageKey);
      if (savedFileNames) {
        try {
          const fileNames = JSON.parse(savedFileNames);
        } catch (error) {
          console.error('Failed to load saved files:', error);
        }
      }
    }, [projectId, fileStatus]);

    // Save file names to localStorage whenever selectedFiles changes
    useEffect(() => {
      if (selectedFiles.length > 0) {
        const storageKey = `attachments_${projectId}_${fileStatus}`;
        const fileNames = selectedFiles.map(f => f.name);
        localStorage.setItem(storageKey, JSON.stringify(fileNames));
      }
    }, [selectedFiles, projectId, fileStatus]);

    // Expose methods to parent component via ref
    useImperativeHandle(ref, () => ({
      triggerUpload: handleSubmit,
      getSelectedFileNames: () => selectedFiles.map((f) => normalizeFileName(f.name)),
      clearFiles: () => {
        setSelectedFiles([]);
        const storageKey = `attachments_${projectId}_${fileStatus}`;
        localStorage.removeItem(storageKey);
        setMismatchedFiles(new Set());
      },
      highlightMismatchedFiles: (fileNames: string[]) => {
        setMismatchedFiles(new Set(fileNames));
      },
    }));

    const checkFileList = useCallback(
      async (prefix: string, projectIdParam: number, fileStatusParam: string, uploadedFiles: FileList) => {
        const totalFileSize = Array.from(uploadedFiles).reduce((total, file) => total + file.size, 0);

        let limitBytes = 0;
        const limitStr = config.PROJECT_FILE_UPLOAD_SIZE_LIMIT.toLowerCase();
        if (limitStr.endsWith("mb")) limitBytes = parseInt(limitStr) * 1024 * 1024;
        else if (limitStr.endsWith("gb")) limitBytes = parseInt(limitStr) * 1024 * 1024 * 1024;
        else limitBytes = parseInt(limitStr);

        await dispatch(checkExistingFiles(prefix, projectIdParam, fileStatusParam) as any);

        const filesToBeOverwritten: string[] = [];
        if (content && typeof content !== "string") {
          content?.fileList?.forEach((savedFile: any) => {
            const savedFileName = savedFile.split("/").splice(-1, 1)[0];
            const normalizedSavedFileName = normalizeFileName(savedFileName);
            const existingFile = find(uploadedFiles, (file) => normalizeFileName(file?.name) === normalizedSavedFileName);
            if (existingFile) filesToBeOverwritten.push(existingFile.name);
          });
        }

        setAnalytics({
          attachmentsCount: uploadedFiles.length,
          fileExtensionList: Array.from(uploadedFiles).map(({ name }) => name.split(".").pop() || ""),
          overwrite: filesToBeOverwritten.length > 0,
          projectName,
          projectType,
          fileStatus: fileStatusParam,
          fileType: "store",
        });

        if (filesToBeOverwritten.length > 0) {
          const confirmed = window.confirm(`You are about to overwrite ${filesToBeOverwritten.length} file(s).`);
          if (!confirmed) return { continue: false };
        }

        return { continue: true };
      },
      [content, projectName, projectType, dispatch]
    );

    const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
      else setDragActive(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const newFiles = Array.from(e.dataTransfer.files);

        // Append new files to existing ones, avoiding duplicates by name
        setSelectedFiles((prevFiles) => {
          const existingFileNames = new Set(prevFiles.map(f => f.name));
          const filesToAdd = newFiles.filter(f => !existingFileNames.has(f.name));
          return [...prevFiles, ...filesToAdd];
        });
        e.dataTransfer.clearData();
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        const newFiles = Array.from(e.target.files);

        // Append new files to existing ones, avoiding duplicates by name
        setSelectedFiles((prevFiles) => {
          const existingFileNames = new Set(prevFiles.map(f => f.name));
          const filesToAdd = newFiles.filter(f => !existingFileNames.has(f.name));
          return [...prevFiles, ...filesToAdd];
        });
      }
      e.target.value = "";
    };

    const handleClick = () => {
      if (!disabled) {
        fileInputRef.current?.click();
      }
    };

    const handleAfterUpload = () => {
      if (analytics) {
        // dispatch(createAnalyticsEvent({
        //   event: EventTypes.UPLOAD_ATTACHMENTS,
        //   eventType: "Action",
        //   projectName: analytics.projectName,
        //   projectType: analytics.projectType,
        //   uploadAttachmentCount: analytics.attachmentsCount,
        //   uploadAttachmentFileExtensionList: analytics.fileExtensionList,
        //   uploadAttachmentStatus: hasError ? Status.FAILURE : Status.SUCCESS,
        //   uploadAttachmentOverwrite: analytics.overwrite,
        //   uploadAttachmentFileType: FileType.STORE,
        //   uploadAttachmentFileStatus: analytics.fileStatus as FileStatus,
        // }));
      }
      setSelectedFiles([]);

      // Clear localStorage after successful upload
      const storageKey = `attachments_${projectId}_${fileStatus}`;
      localStorage.removeItem(storageKey);

      setIsUploading(false);
      setFinalUploadMessage("File(s) have been uploaded successfully.");

      // Clear store uploads after completion
      setTimeout(() => {
        dispatch(clearStoreUploads());
        setFinalUploadMessage("");
      }, 3000);
    };

    const BATCH_SIZE = 200; // safe limit
    const CHUNK_SIZE = 8 * 1024 * 1024; // 8MB

// 🔹 Upload single chunk
    const uploadChunk = async (
      chunk: Blob,
      sasUrl: string,
      blockId: string,
      blobAxios: any
    ) => {
      const url = `${sasUrl}&comp=block&blockid=${encodeURIComponent(blockId)}`;

      await blobAxios.put(url, chunk, {
        headers: {
          "x-ms-version": "2020-10-02",
          "Content-Type": "application/octet-stream",
        },
      });
    };

// 🔹 Get SAS URLs in PARALLEL batches
    const getSasUrlsInParallelBatches = async (
      fileNames: string[],
      dispatch: any,
      projectId: number,
      chainId: number,
      fileStatus: string,
      userId: string
    ) => {
      const batches = [];

      for (let i = 0; i < fileNames.length; i += BATCH_SIZE) {
        batches.push(fileNames.slice(i, i + BATCH_SIZE));
      }

      const responses = await Promise.all(
        batches.map((batch) =>
          dispatch(
            getSASURLUpload(
              "ATTACHMENT",
              Number(projectId),
              chainId,
              fileStatus,
              batch,
              userId
            ) as any
          )
        )
      );

      return responses.flatMap(
        (res) => res?.sasURLs || res?.payload?.sasURLs || []
      );
    };


    const handleSubmit = async () => {
      if (disabled) {
        alert("Attachment upload is disabled.");
        return;
      }
      if (selectedFiles.length === 0) {
        alert("Please select at least one file.");
        return;
      }
      if (!project?.chain?.id) {
        alert("Project information not available.");
        return;
      }

      setIsUploading(true);

      // Initialize store upload state
      dispatch(setStoreUploadProject(project));
      dispatch(setStoreLoading(true));
      dispatch(setStoreError(false));

      const dataTransfer = new DataTransfer();
      selectedFiles.forEach((file) => dataTransfer.items.add(file));

      try {
        const result = await checkFileList("ATTACHMENT", Number(projectId), fileStatus, dataTransfer.files);
        if (!result?.continue) {
          setIsUploading(false);
          dispatch(setStoreLoading(false));
          return;
        }

        // Normalize file names before sending to API
        const fileNames = selectedFiles.map((f) => normalizeFileName(f.name));
        const files = [...selectedFiles];

        const uploadResult = await dispatch(getSASURLUpload("ATTACHMENT", Number(projectId), project.chain.id, fileStatus, fileNames, userId) as any);

        const sasURLs = uploadResult?.sasURLs || uploadResult?.payload?.sasURLs || [];
        const chainId = uploadResult?.chainId || uploadResult?.payload?.chainId || project.chain.id;

        if (sasURLs.length > 0) {
          // Generate unique IDs for all files BEFORE starting upload
          const fileIds = files.map((file, idx) => `${Date.now()}-${idx}-${normalizeFileName(file?.name)}`);

          // Initialize progress for ALL files first
          fileIds.forEach((fileId, idx) => {
            dispatch(setStoreUploadProgress({
              id: fileId,
              name: normalizeFileName(files[idx].name),
              percent: 0
            }));
          });

          for (let i = 0; i < sasURLs.length; i++) {
            const sasFile = sasURLs[i];
            const file = files[i];
            const fileId = fileIds[i]; // Use pre-generated ID
            const normalizedFileName = normalizeFileName(file?.name);

            if (!sasFile?.url || !sasFile?.fileName) continue;

            if (sasFile.fileName === normalizedFileName) {

              const blobAxios = axios.create();
              await blobAxios.put(sasFile.url, "", { headers: { "x-ms-blob-type": "BlockBlob" } });

              const chunkSize = 8 * 1024 * 1024;
              const blockIds: string[] = [];
              const totalChunks = Math.ceil(file.size / chunkSize);

              for (let start = 0; start < file.size; start += chunkSize) {
                const chunk = file.slice(start, start + chunkSize);
                const blockNumber = blockIds.length + 1;
                const blockId = btoa(`block-${String(blockNumber).padStart(6, "0")}`);
                blockIds.push(blockId);

                await uploadChunk(chunk, sasFile.url, blockId, blobAxios);

                // Update progress after each chunk
                const percentComplete = Math.round((blockNumber / totalChunks) * 100);
                dispatch(setStoreUploadProgress({
                  id: fileId,
                  name: normalizedFileName,
                  percent: Math.min(percentComplete, 95) // Keep at 95% until commit
                }));
              }

              await dispatch(updateUploadedFiles(projectId, fileStatus, normalizedFileName, chainId) as any);

              const blockListXml = `<?xml version="1.0" encoding="utf-8"?><BlockList>${blockIds.map((id) => `<Latest>${id}</Latest>`).join("")}</BlockList>`;
              const commitUrl = new URL(sasFile.url);
              commitUrl.searchParams.append("comp", "blocklist");

              if (blobAxios.defaults.headers.common["Authorization"]) delete blobAxios.defaults.headers.common["Authorization"];

              await blobAxios.put(commitUrl.toString(), blockListXml, {
                headers: {
                  "x-ms-version": "2020-10-02",
                  "Content-Type": "application/xml",
                  "x-ms-blob-content-type": file.type || "application/octet-stream",
                },
              });

              dispatch(setStoreUploadProgress({
                id: fileId,
                name: normalizedFileName,
                percent: 100
              }));
            }
          }
          await dispatch(fetchProject(projectId) as any);
          await dispatch(fetchAllProjectFiles(Number(projectId)) as any);
        }

        dispatch(setStoreLoading(false));
        handleAfterUpload();
      } catch (err) {
        dispatch(fileCheckFailed("ATTACHMENT", err instanceof Error ? err.message : "Upload failed"));
        dispatch(setStoreError(true));
        dispatch(setStoreLoading(false));
        setIsUploading(false);
      }
    };

    const getTotalFileSize = () => (selectedFiles.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(2);

    return (
      <div style={{ padding: 0 }}>
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleClick}
          style={{
            border: dragActive ? "3px solid #4CAF50" : "2px dashed #ccc",
            borderRadius: "8px",
            padding: "40px",
            textAlign: "center",
            cursor: disabled ? "not-allowed" : "pointer",
            backgroundColor: dragActive ? "#f0f8f0" : "#fafafa",
            transition: "all 0.3s ease",
            margin: "15px 0 0 0",
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleChange}
            accept={acceptedStoreFileTypes}
            style={{ display: "none" }}
            disabled={disabled}
          />
          <Folder size={32} style={{ margin: "0 auto 10px", color: "#ccc" }} />
          <p>
            Drag and drop Planogram and PDF files here, or
            <span style={{ color: "#E5B42F", marginRight: 5 }}> click to upload</span>
          </p>
        </div>

        {/* Show selected files before upload */}
        {selectedFiles.length > 0 && !isUploading && (
          <div
            style={{
              marginTop: 15,
              padding: "12px",
              borderRadius: "8px",
              background: "#f9f9f9",
              border: "1px solid #e0e0e0",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <Typography style={{ fontSize: 12, fontWeight: 600, color: "#444" }}>
                Selected Files ({selectedFiles.length})
              </Typography>
              <button
                onClick={() => {
                  setSelectedFiles([]);
                  const storageKey = `attachments_${projectId}_${fileStatus}`;
                  localStorage.removeItem(storageKey);
                  setMismatchedFiles(new Set());
                }}
                style={{
                  padding: "6px 12px",
                  backgroundColor: "#E5B611",
                  color: "black",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px"
                }}
              >
                Clear All
              </button>
            </div>
            <div
              style={{
                maxHeight: selectedFiles.length > 10 ? "280px" : "auto",
                overflowY: selectedFiles.length > 10 ? "scroll" : "visible",
                overflowX: "hidden",
                paddingRight: selectedFiles.length > 10 ? "8px" : "0",
              }}
            >
              <ul
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: "10px",
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                }}
              >
                {selectedFiles.map((file, index) => {
                  const isMismatched = mismatchedFiles.has(file?.name);
                  return (
                  <li
                    key={index}
                    style={{
                      padding: "10px",
                      background: isMismatched ? "#ffe5e5" : "#fff",
                      borderRadius: 6,
                      border: isMismatched ? "2px solid #ff4d4f" : "1px solid #e8e8e8",
                      fontSize: 11,
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      minHeight: "70px",
                      boxShadow: isMismatched ? "0 2px 6px rgba(255, 77, 79, 0.2)" : "0 1px 3px rgba(0,0,0,0.05)",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isMismatched) {
                        e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.1)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isMismatched) {
                        e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.05)";
                      }
                    }}
                  >
                    <button
                      onClick={() => {
                        // Remove file from selectedFiles state
                        const updatedFiles = selectedFiles.filter((_, i) => i !== index);
                        setSelectedFiles(updatedFiles);

                        // Update localStorage with remaining files
                        const storageKey = `attachments_${projectId}_${fileStatus}`;
                        if (updatedFiles.length > 0) {
                          const fileNames = updatedFiles.map(f => f.name);
                          localStorage.setItem(storageKey, JSON.stringify(fileNames));
                        } else {
                          // If no files left, remove the key from localStorage
                          localStorage.removeItem(storageKey);
                        }

                        // Remove from mismatched files set
                        setMismatchedFiles((prev) => {
                          const newSet = new Set(prev);
                          newSet.delete(file?.name);
                          return newSet;
                        });
                      }}
                      style={{
                        position: "absolute",
                        top: "6px",
                        right: "6px",
                        background: "#fff",
                        border: `1px solid ${isMismatched ? "#ff4d4f" : "#E5B611"}`,
                        borderRadius: "50%",
                        color: isMismatched ? "#ff4d4f" : "#E5B611",
                        cursor: "pointer",
                        fontSize: "12px",
                        padding: "0",
                        fontWeight: "bold",
                        lineHeight: 1,
                        width: "18px",
                        height: "18px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (isMismatched) {
                          e.currentTarget.style.background = "#ff4d4f";
                          e.currentTarget.style.color = "#fff";
                        } else {
                          e.currentTarget.style.background = "#E5B611";
                          e.currentTarget.style.color = "#000";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#fff";
                        e.currentTarget.style.color = isMismatched ? "#ff4d4f" : "#E5B611";
                      }}
                      title="Remove file"
                    >
                      ×
                    </button>
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        marginBottom: "6px",
                        paddingRight: "20px",
                        fontWeight: 500,
                        color: isMismatched ? "#ff4d4f" : "#333",
                      }}
                      title={file?.name}
                    >
                      {isMismatched ? "⚠️" : "📎"} {file?.name}
                    </span>
                    <span style={{ fontSize: 10, color: isMismatched ? "#ff4d4f" : "#666", marginTop: "auto" }}>
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                  </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}

        {/* Show uploading files with progress */}
        {uploadedFiles.length > 0 && (
          <div
            style={{
              marginTop: 20,
              padding: "15px",
              borderRadius: "12px",
              background: "#ffffff",
              boxShadow: "0px 1px 4px rgba(0,0,0,0.1)",
            }}
          >
            <Typography style={{ fontSize: 12, marginBottom: 8, fontWeight: 600, color: "#444" }}>
              Uploading Store Files
            </Typography>
            <ul
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
                listStyle: "none",
                padding: 0,
                margin: 0,
              }}
            >
              {uploadedFiles.map((file: any, index: any) => {
                const percent = uploadProgress[file.id] || 0;
                const isComplete = percent === 100;
                return (
                  <li
                    key={index}
                    style={{
                      position: "relative",
                      flex: "0 0 calc(33.33% - 8px)",
                      padding: "8px 12px",
                      background: "#f9f9f9",
                      borderRadius: 8,
                      border: "1px solid #eee",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {isComplete && (
                      <span
                        onClick={() => dispatch(clearStoreUploads())}
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          cursor: "pointer",
                          fontWeight: "bold",
                          color: "#ff4d4f",
                          fontSize: 14,
                        }}
                      >
                        ✕
                      </span>
                    )}
                    <span
                      style={{
                        fontSize: 12,
                        marginBottom: 4,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      📎 {file?.name}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {loading && <div style={{ textAlign: "center", padding: "20px" }}><img className="spin-right" src={loadingSvg} alt="Loading" /></div>}
        {hasError && <div style={{ padding: "12px", backgroundColor: "#ffebee", border: "1px solid #ef5350", borderRadius: "4px", marginTop: "16px" }}><span style={{ color: "#c62828", fontWeight: 500 }}>Upload Error{errors ? `: ${errors[0]}` : ""}</span></div>}
        {content === "OK" && <div style={{ padding: "16px", backgroundColor: "#e8f5e9", border: "1px solid #4CAF50", borderRadius: "4px", marginTop: "16px" }}><div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}><img src={successSvg} alt="Success" style={{ marginRight: "8px" }} /><div style={{ color: "#2e7d32", fontWeight: 600 }}>Successfully uploaded files.</div></div></div>}
        {finalUploadMessage && <div style={{ marginTop: "20px", padding: "12px 16px", backgroundColor: "#e8f5e9", border: "1px solid #4CAF50", borderRadius: "6px", color: "#2e7d32", fontWeight: 600, textAlign: "center" }}>{finalUploadMessage}</div>}
      </div>
    );
  }
);

Step2UploadAttachments.displayName = "Step2UploadAttachments";
export default Step2UploadAttachments;
